import { z } from "zod"
import { TranslationKey, Translator } from "../i18n/translation-key"
import { JobOrigin } from "./job"
import { JobSummary } from "./job-summary"
import { BookingType } from "./service-booking"

/**
 * "Alla jobb" table views. A view is a store-wide, named configuration of
 * which columns to show, how to sort, and how to filter jobs. Filtering,
 * sorting and pagination are all evaluated by the backend from the stored
 * config, so every user of the store sees the same rows.
 */

export const JOB_TABLE_TEXT_FILTER_COLUMNS = [
  "customerName",
  "customerPhone",
  "customerEmail",
  "customerComment",
  "bikeBrand",
  "bikeModel",
  "bikeColor",
  "reference",
  "leasingId",
] as const

export const JOB_TABLE_NUMBER_FILTER_COLUMNS = [
  "jobNumber",
  "physicalTag",
  "totalOrderValue",
  "totalLaborCost",
  "totalProductCost",
  "totalDiscountGiven",
  "invoiceNumber",
] as const

export const JOB_TABLE_DATE_FILTER_COLUMNS = [
  "handInDate",
  "createdAt",
  "updatedAt",
  "invoicedAt",
] as const

export const JOB_TABLE_OPTION_FILTER_COLUMNS = [
  "status",
  "origin",
  "bookingType",
  "bikeType",
  "labels",
  "packages",
  "assignedMechanic",
  "receptionMechanic",
  "inspectionMechanic",
  "company",
  "department",
] as const

export const JOB_TABLE_BOOLEAN_FILTER_COLUMNS = [
  "isElectric",
  "isTemporarilyHandedOut",
  "hasCompletedInspection",
  "hasSentAddonOrder",
  "isInvoiced",
] as const

/** Columns that can be shown but not sorted or filtered on (derived data). */
export const JOB_TABLE_DISPLAY_ONLY_COLUMNS = [
  "customerMessage",
  "internalNote",
  "contactPerson",
  "isWaitingForParts",
  "numMessages",
  "reminder",
  "work",
  "collectedAt",
  "proposalSentAt",
  "proposalAcceptedAt",
  "finishedAt",
] as const

export const JOB_TABLE_COLUMN_KEYS = [
  ...JOB_TABLE_TEXT_FILTER_COLUMNS,
  ...JOB_TABLE_NUMBER_FILTER_COLUMNS,
  ...JOB_TABLE_DATE_FILTER_COLUMNS,
  ...JOB_TABLE_OPTION_FILTER_COLUMNS,
  ...JOB_TABLE_BOOLEAN_FILTER_COLUMNS,
  ...JOB_TABLE_DISPLAY_ONLY_COLUMNS,
] as const

export const JOB_TABLE_SORTABLE_COLUMNS = [
  "jobNumber",
  "status",
  "physicalTag",
  "handInDate",
  "createdAt",
  "updatedAt",
  "invoicedAt",
  "customerName",
  "bikeBrand",
  "assignedMechanic",
  "totalOrderValue",
  "totalLaborCost",
  "totalProductCost",
  "totalDiscountGiven",
] as const

export type JobTableTextFilterColumn =
  (typeof JOB_TABLE_TEXT_FILTER_COLUMNS)[number]
export type JobTableNumberFilterColumn =
  (typeof JOB_TABLE_NUMBER_FILTER_COLUMNS)[number]
export type JobTableDateFilterColumn =
  (typeof JOB_TABLE_DATE_FILTER_COLUMNS)[number]
export type JobTableOptionFilterColumn =
  (typeof JOB_TABLE_OPTION_FILTER_COLUMNS)[number]
export type JobTableBooleanFilterColumn =
  (typeof JOB_TABLE_BOOLEAN_FILTER_COLUMNS)[number]
export type JobTableColumnKey = (typeof JOB_TABLE_COLUMN_KEYS)[number]
export type JobTableSortableColumn = (typeof JOB_TABLE_SORTABLE_COLUMNS)[number]

export const JOB_TABLE_COLUMN_LABEL_KEYS: Record<
  JobTableColumnKey,
  TranslationKey
> = {
  jobNumber: "common.jobTable.columns.jobNumber",
  status: "common.jobTable.columns.status",
  physicalTag: "common.jobTable.columns.physicalTag",
  origin: "common.jobTable.columns.origin",
  bookingType: "common.jobTable.columns.bookingType",
  labels: "common.jobTable.columns.labels",
  packages: "common.jobTable.columns.packages",
  isTemporarilyHandedOut: "common.jobTable.columns.isTemporarilyHandedOut",
  hasCompletedInspection: "common.jobTable.columns.hasCompletedInspection",
  hasSentAddonOrder: "common.jobTable.columns.hasSentAddonOrder",
  handInDate: "common.jobTable.columns.handInDate",
  createdAt: "common.jobTable.columns.createdAt",
  updatedAt: "common.jobTable.columns.updatedAt",
  invoicedAt: "common.jobTable.columns.invoicedAt",
  customerName: "common.jobTable.columns.customerName",
  customerPhone: "common.jobTable.columns.customerPhone",
  customerEmail: "common.jobTable.columns.customerEmail",
  customerComment: "common.jobTable.columns.customerComment",
  customerMessage: "common.jobTable.columns.customerMessage",
  internalNote: "common.jobTable.columns.internalNote",
  bikeBrand: "common.jobTable.columns.bikeBrand",
  bikeType: "common.jobTable.columns.bikeType",
  bikeModel: "common.jobTable.columns.bikeModel",
  bikeColor: "common.jobTable.columns.bikeColor",
  isElectric: "common.jobTable.columns.isElectric",
  assignedMechanic: "common.jobTable.columns.assignedMechanic",
  receptionMechanic: "common.jobTable.columns.receptionMechanic",
  inspectionMechanic: "common.jobTable.columns.inspectionMechanic",
  totalOrderValue: "common.jobTable.columns.totalOrderValue",
  totalLaborCost: "common.jobTable.columns.totalLaborCost",
  totalProductCost: "common.jobTable.columns.totalProductCost",
  totalDiscountGiven: "common.jobTable.columns.totalDiscountGiven",
  company: "common.jobTable.columns.company",
  department: "common.jobTable.columns.department",
  contactPerson: "common.jobTable.columns.contactPerson",
  reference: "common.jobTable.columns.reference",
  leasingId: "common.jobTable.columns.leasingId",
  invoiceNumber: "common.jobTable.columns.invoiceNumber",
  isInvoiced: "common.jobTable.columns.isInvoiced",
  isWaitingForParts: "common.jobTable.columns.isWaitingForParts",
  numMessages: "common.jobTable.columns.numMessages",
  reminder: "common.jobTable.columns.reminder",
  work: "common.jobTable.columns.work",
  collectedAt: "common.jobTable.columns.collectedAt",
  proposalSentAt: "common.jobTable.columns.proposalSentAt",
  proposalAcceptedAt: "common.jobTable.columns.proposalAcceptedAt",
  finishedAt: "common.jobTable.columns.finishedAt",
}

export const JOB_TABLE_COLUMN_CATEGORIES = [
  "job",
  "dates",
  "customer",
  "bike",
  "mechanic",
  "price",
  "company",
  "invoice",
] as const

export type JobTableColumnCategory =
  (typeof JOB_TABLE_COLUMN_CATEGORIES)[number]

export const JOB_TABLE_COLUMN_CATEGORY_LABEL_KEYS: Record<
  JobTableColumnCategory,
  TranslationKey
> = {
  job: "common.jobTable.categories.job",
  dates: "common.jobTable.categories.dates",
  customer: "common.jobTable.categories.customer",
  bike: "common.jobTable.categories.bike",
  mechanic: "common.jobTable.categories.mechanic",
  price: "common.jobTable.categories.price",
  company: "common.jobTable.categories.company",
  invoice: "common.jobTable.categories.invoice",
}

/**
 * Where each column belongs and its name within that category. Pickers show
 * "<category> – <name>" (e.g. "Kund – Telefon"); table headers keep the
 * standalone label from JOB_TABLE_COLUMN_LABELS.
 */
export const JOB_TABLE_COLUMN_GROUPING: Record<
  JobTableColumnKey,
  { category: JobTableColumnCategory; nameKey: TranslationKey }
> = {
  jobNumber: {
    category: "job",
    nameKey: "common.jobTable.columnNames.jobNumber",
  },
  status: { category: "job", nameKey: "common.jobTable.columnNames.status" },
  physicalTag: {
    category: "job",
    nameKey: "common.jobTable.columnNames.physicalTag",
  },
  origin: { category: "job", nameKey: "common.jobTable.columnNames.origin" },
  bookingType: {
    category: "job",
    nameKey: "common.jobTable.columnNames.bookingType",
  },
  labels: { category: "job", nameKey: "common.jobTable.columnNames.labels" },
  leasingId: {
    category: "job",
    nameKey: "common.jobTable.columnNames.leasingId",
  },
  packages: {
    category: "job",
    nameKey: "common.jobTable.columnNames.packages",
  },
  isTemporarilyHandedOut: {
    category: "job",
    nameKey: "common.jobTable.columnNames.isTemporarilyHandedOut",
  },
  hasCompletedInspection: {
    category: "job",
    nameKey: "common.jobTable.columnNames.hasCompletedInspection",
  },
  hasSentAddonOrder: {
    category: "job",
    nameKey: "common.jobTable.columnNames.hasSentAddonOrder",
  },
  isWaitingForParts: {
    category: "job",
    nameKey: "common.jobTable.columnNames.isWaitingForParts",
  },
  numMessages: {
    category: "job",
    nameKey: "common.jobTable.columnNames.numMessages",
  },
  reminder: {
    category: "job",
    nameKey: "common.jobTable.columnNames.reminder",
  },
  work: { category: "job", nameKey: "common.jobTable.columnNames.work" },
  internalNote: {
    category: "job",
    nameKey: "common.jobTable.columnNames.internalNote",
  },
  handInDate: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.handInDate",
  },
  createdAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.createdAt",
  },
  updatedAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.updatedAt",
  },
  collectedAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.collectedAt",
  },
  proposalSentAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.proposalSentAt",
  },
  proposalAcceptedAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.proposalAcceptedAt",
  },
  finishedAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.finishedAt",
  },
  invoicedAt: {
    category: "dates",
    nameKey: "common.jobTable.columnNames.invoicedAt",
  },
  customerName: {
    category: "customer",
    nameKey: "common.jobTable.columnNames.customerName",
  },
  customerPhone: {
    category: "customer",
    nameKey: "common.jobTable.columnNames.customerPhone",
  },
  customerEmail: {
    category: "customer",
    nameKey: "common.jobTable.columnNames.customerEmail",
  },
  customerComment: {
    category: "customer",
    nameKey: "common.jobTable.columnNames.customerComment",
  },
  customerMessage: {
    category: "customer",
    nameKey: "common.jobTable.columnNames.customerMessage",
  },
  bikeBrand: {
    category: "bike",
    nameKey: "common.jobTable.columnNames.bikeBrand",
  },
  bikeType: {
    category: "bike",
    nameKey: "common.jobTable.columnNames.bikeType",
  },
  bikeModel: {
    category: "bike",
    nameKey: "common.jobTable.columnNames.bikeModel",
  },
  bikeColor: {
    category: "bike",
    nameKey: "common.jobTable.columnNames.bikeColor",
  },
  isElectric: {
    category: "bike",
    nameKey: "common.jobTable.columnNames.isElectric",
  },
  assignedMechanic: {
    category: "mechanic",
    nameKey: "common.jobTable.columnNames.assignedMechanic",
  },
  receptionMechanic: {
    category: "mechanic",
    nameKey: "common.jobTable.columnNames.receptionMechanic",
  },
  inspectionMechanic: {
    category: "mechanic",
    nameKey: "common.jobTable.columnNames.inspectionMechanic",
  },
  totalOrderValue: {
    category: "price",
    nameKey: "common.jobTable.columnNames.totalOrderValue",
  },
  totalLaborCost: {
    category: "price",
    nameKey: "common.jobTable.columnNames.totalLaborCost",
  },
  totalProductCost: {
    category: "price",
    nameKey: "common.jobTable.columnNames.totalProductCost",
  },
  totalDiscountGiven: {
    category: "price",
    nameKey: "common.jobTable.columnNames.totalDiscountGiven",
  },
  company: {
    category: "company",
    nameKey: "common.jobTable.columnNames.company",
  },
  department: {
    category: "company",
    nameKey: "common.jobTable.columnNames.department",
  },
  contactPerson: {
    category: "company",
    nameKey: "common.jobTable.columnNames.contactPerson",
  },
  reference: {
    category: "company",
    nameKey: "common.jobTable.columnNames.reference",
  },
  invoiceNumber: {
    category: "invoice",
    nameKey: "common.jobTable.columnNames.invoiceNumber",
  },
  isInvoiced: {
    category: "invoice",
    nameKey: "common.jobTable.columnNames.isInvoiced",
  },
}

/**
 * "Kund – Telefon" style label used in the column, filter and sort pickers.
 * The job's own columns have no prefix — every row already is a job.
 */
export function getJobTableColumnPickerLabel(
  column: JobTableColumnKey,
  t: Translator,
): string {
  const { category, nameKey } = JOB_TABLE_COLUMN_GROUPING[column]
  if (category === "job") return t(nameKey)
  return t("common.jobTable.columnPickerLabel", {
    category: t(JOB_TABLE_COLUMN_CATEGORY_LABEL_KEYS[category]),
    name: t(nameKey),
  })
}

/** Orders columns by category, then by their order within the category. */
export function sortJobTableColumnsByCategory<T extends JobTableColumnKey>(
  columns: readonly T[],
): T[] {
  const groupingOrder = Object.keys(JOB_TABLE_COLUMN_GROUPING)
  const rank = (column: T) =>
    JOB_TABLE_COLUMN_CATEGORIES.indexOf(
      JOB_TABLE_COLUMN_GROUPING[column].category,
    ) *
      1000 +
    groupingOrder.indexOf(column)
  return [...columns].sort((a, b) => rank(a) - rank(b))
}

export const JOB_ORIGIN_LABEL_KEYS: Record<JobOrigin, TranslationKey> = {
  [JobOrigin.JULO_BOOKING_BY_CUSTOMER]:
    "common.jobOrigin.juloBookingByCustomer",
  [JobOrigin.STORE_SITE_BOOKING_BY_CUSTOMER]:
    "common.jobOrigin.storeSiteBookingByCustomer",
  [JobOrigin.EVENT_BOOKING_BY_CUSTOMER]:
    "common.jobOrigin.eventBookingByCustomer",
  [JobOrigin.DROP_IN_BY_MECHANIC]: "common.jobOrigin.dropInByMechanic",
  [JobOrigin.BOOKING_BY_MECHANIC]: "common.jobOrigin.bookingByMechanic",
  [JobOrigin.NEW_MANUAL_RECEPTION]: "common.jobOrigin.newManualReception",
  [JobOrigin.DROP_IN_BY_CUSTOMER_CHECK_IN]:
    "common.jobOrigin.dropInByCustomerCheckIn",
}

export const JOB_BOOKING_TYPE_LABEL_KEYS: Record<BookingType, TranslationKey> =
  {
    [BookingType.MAINTENANCE]: "common.bookingType.maintenance",
    [BookingType.EVENT]: "common.bookingType.event",
  }

const TEXT_FILTER_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_TEXT_FILTER_COLUMNS,
)
const NUMBER_FILTER_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_NUMBER_FILTER_COLUMNS,
)
const DATE_FILTER_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_DATE_FILTER_COLUMNS,
)
const OPTION_FILTER_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_OPTION_FILTER_COLUMNS,
)
const BOOLEAN_FILTER_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_BOOLEAN_FILTER_COLUMNS,
)
const SORTABLE_COLUMN_SET: ReadonlySet<string> = new Set(
  JOB_TABLE_SORTABLE_COLUMNS,
)

export function isJobTableTextFilterColumn(
  value: string,
): value is JobTableTextFilterColumn {
  return TEXT_FILTER_COLUMN_SET.has(value)
}

export function isJobTableNumberFilterColumn(
  value: string,
): value is JobTableNumberFilterColumn {
  return NUMBER_FILTER_COLUMN_SET.has(value)
}

export function isJobTableDateFilterColumn(
  value: string,
): value is JobTableDateFilterColumn {
  return DATE_FILTER_COLUMN_SET.has(value)
}

export function isJobTableOptionFilterColumn(
  value: string,
): value is JobTableOptionFilterColumn {
  return OPTION_FILTER_COLUMN_SET.has(value)
}

export function isJobTableBooleanFilterColumn(
  value: string,
): value is JobTableBooleanFilterColumn {
  return BOOLEAN_FILTER_COLUMN_SET.has(value)
}

export function isJobTableSortableColumn(
  value: string,
): value is JobTableSortableColumn {
  return SORTABLE_COLUMN_SET.has(value)
}

// ── Filter operators ────────────────────────────────────────────────────────

export const JOB_TABLE_TEXT_OPERATORS = [
  "contains",
  "notContains",
  "is",
  "isNot",
  "isEmpty",
  "isNotEmpty",
] as const

export const JOB_TABLE_NUMBER_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "isEmpty",
  "isNotEmpty",
] as const

export const JOB_TABLE_DATE_OPERATORS = [
  "is",
  "before",
  "after",
  "onOrBefore",
  "onOrAfter",
  "isToday",
  "isPastWeek",
  "isPastMonth",
  "isNextWeek",
  "isEmpty",
  "isNotEmpty",
] as const

export const JOB_TABLE_OPTION_OPERATORS = [
  "isAnyOf",
  "isNoneOf",
  "isEmpty",
  "isNotEmpty",
] as const

export const JOB_TABLE_BOOLEAN_OPERATORS = ["is"] as const

export type JobTableTextOperator = (typeof JOB_TABLE_TEXT_OPERATORS)[number]
export type JobTableNumberOperator = (typeof JOB_TABLE_NUMBER_OPERATORS)[number]
export type JobTableDateOperator = (typeof JOB_TABLE_DATE_OPERATORS)[number]
export type JobTableOptionOperator = (typeof JOB_TABLE_OPTION_OPERATORS)[number]
export type JobTableBooleanOperator =
  (typeof JOB_TABLE_BOOLEAN_OPERATORS)[number]

export const JOB_TABLE_TEXT_OPERATOR_LABEL_KEYS: Record<
  JobTableTextOperator,
  TranslationKey
> = {
  contains: "common.jobTable.operators.text.contains",
  notContains: "common.jobTable.operators.text.notContains",
  is: "common.jobTable.operators.text.is",
  isNot: "common.jobTable.operators.text.isNot",
  isEmpty: "common.jobTable.operators.text.isEmpty",
  isNotEmpty: "common.jobTable.operators.text.isNotEmpty",
}

export const JOB_TABLE_NUMBER_OPERATOR_LABEL_KEYS: Record<
  JobTableNumberOperator,
  TranslationKey
> = {
  eq: "common.jobTable.operators.number.eq",
  neq: "common.jobTable.operators.number.neq",
  gt: "common.jobTable.operators.number.gt",
  gte: "common.jobTable.operators.number.gte",
  lt: "common.jobTable.operators.number.lt",
  lte: "common.jobTable.operators.number.lte",
  isEmpty: "common.jobTable.operators.number.isEmpty",
  isNotEmpty: "common.jobTable.operators.number.isNotEmpty",
}

export const JOB_TABLE_DATE_OPERATOR_LABEL_KEYS: Record<
  JobTableDateOperator,
  TranslationKey
> = {
  is: "common.jobTable.operators.date.is",
  before: "common.jobTable.operators.date.before",
  after: "common.jobTable.operators.date.after",
  onOrBefore: "common.jobTable.operators.date.onOrBefore",
  onOrAfter: "common.jobTable.operators.date.onOrAfter",
  isToday: "common.jobTable.operators.date.isToday",
  isPastWeek: "common.jobTable.operators.date.isPastWeek",
  isPastMonth: "common.jobTable.operators.date.isPastMonth",
  isNextWeek: "common.jobTable.operators.date.isNextWeek",
  isEmpty: "common.jobTable.operators.date.isEmpty",
  isNotEmpty: "common.jobTable.operators.date.isNotEmpty",
}

export const JOB_TABLE_OPTION_OPERATOR_LABEL_KEYS: Record<
  JobTableOptionOperator,
  TranslationKey
> = {
  isAnyOf: "common.jobTable.operators.option.isAnyOf",
  isNoneOf: "common.jobTable.operators.option.isNoneOf",
  isEmpty: "common.jobTable.operators.option.isEmpty",
  isNotEmpty: "common.jobTable.operators.option.isNotEmpty",
}

export const JOB_TABLE_BOOLEAN_OPERATOR_LABEL_KEYS: Record<
  JobTableBooleanOperator,
  TranslationKey
> = {
  is: "common.jobTable.operators.boolean.is",
}

/** Operators that don't use the rule's value. */
const VALUELESS_OPERATORS: ReadonlySet<string> = new Set([
  "isEmpty",
  "isNotEmpty",
  "isToday",
  "isPastWeek",
  "isPastMonth",
  "isNextWeek",
])

export function isJobTableValuelessOperator(operator: string): boolean {
  return VALUELESS_OPERATORS.has(operator)
}

// ── Filter schema ───────────────────────────────────────────────────────────

const DateKeySchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}$/,
    "common.validation.dateFormat" satisfies TranslationKey,
  )

export const JobTableTextFilterRuleSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("text"),
  column: z.enum(JOB_TABLE_TEXT_FILTER_COLUMNS),
  operator: z.enum(JOB_TABLE_TEXT_OPERATORS),
  value: z.string().max(200),
})

export const JobTableNumberFilterRuleSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("number"),
  column: z.enum(JOB_TABLE_NUMBER_FILTER_COLUMNS),
  operator: z.enum(JOB_TABLE_NUMBER_OPERATORS),
  value: z.number().finite().nullable(),
})

export const JobTableDateFilterRuleSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("date"),
  column: z.enum(JOB_TABLE_DATE_FILTER_COLUMNS),
  operator: z.enum(JOB_TABLE_DATE_OPERATORS),
  value: DateKeySchema.nullable(),
})

export const JobTableOptionFilterRuleSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("options"),
  column: z.enum(JOB_TABLE_OPTION_FILTER_COLUMNS),
  operator: z.enum(JOB_TABLE_OPTION_OPERATORS),
  value: z.array(z.string().min(1)).max(200),
})

export const JobTableBooleanFilterRuleSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("boolean"),
  column: z.enum(JOB_TABLE_BOOLEAN_FILTER_COLUMNS),
  operator: z.enum(JOB_TABLE_BOOLEAN_OPERATORS),
  value: z.boolean(),
})

export const JobTableFilterRuleSchema = z.discriminatedUnion("kind", [
  JobTableTextFilterRuleSchema,
  JobTableNumberFilterRuleSchema,
  JobTableDateFilterRuleSchema,
  JobTableOptionFilterRuleSchema,
  JobTableBooleanFilterRuleSchema,
])

export const JobTableConjunctionSchema = z.enum(["and", "or"])

/**
 * A nested group only holds rules — filters are at most two levels deep
 * (root group → nested group → rules), same as Notion.
 */
export const JobTableFilterGroupSchema = z.object({
  id: z.string().min(1),
  kind: z.literal("group"),
  conjunction: JobTableConjunctionSchema,
  rules: z.array(JobTableFilterRuleSchema).max(50),
})

export const JobTableRootFilterSchema = z.object({
  conjunction: JobTableConjunctionSchema,
  children: z
    .array(
      z.discriminatedUnion("kind", [
        JobTableTextFilterRuleSchema,
        JobTableNumberFilterRuleSchema,
        JobTableDateFilterRuleSchema,
        JobTableOptionFilterRuleSchema,
        JobTableBooleanFilterRuleSchema,
        JobTableFilterGroupSchema,
      ]),
    )
    .max(50),
})

export const JobTableSortSchema = z.object({
  column: z.enum(JOB_TABLE_SORTABLE_COLUMNS),
  direction: z.enum(["asc", "desc"]),
})

export const JOB_TABLE_PAGE_SIZES = [25, 50, 100] as const

export const JobTablePageSizeSchema = z.union([
  z.literal(25),
  z.literal(50),
  z.literal(100),
])

export const JobTableViewConfigSchema = z.object({
  columns: z.array(z.enum(JOB_TABLE_COLUMN_KEYS)).max(100),
  sorts: z.array(JobTableSortSchema).max(10),
  filter: JobTableRootFilterSchema,
  pageSize: JobTablePageSizeSchema,
})

export type JobTableTextFilterRule = z.infer<
  typeof JobTableTextFilterRuleSchema
>
export type JobTableNumberFilterRule = z.infer<
  typeof JobTableNumberFilterRuleSchema
>
export type JobTableDateFilterRule = z.infer<
  typeof JobTableDateFilterRuleSchema
>
export type JobTableOptionFilterRule = z.infer<
  typeof JobTableOptionFilterRuleSchema
>
export type JobTableBooleanFilterRule = z.infer<
  typeof JobTableBooleanFilterRuleSchema
>
export type JobTableFilterRule = z.infer<typeof JobTableFilterRuleSchema>
export type JobTableConjunction = z.infer<typeof JobTableConjunctionSchema>
export type JobTableFilterGroup = z.infer<typeof JobTableFilterGroupSchema>
export type JobTableRootFilter = z.infer<typeof JobTableRootFilterSchema>
export type JobTableFilterNode = JobTableRootFilter["children"][number]
export type JobTableSort = z.infer<typeof JobTableSortSchema>
export type JobTablePageSize = z.infer<typeof JobTablePageSizeSchema>
export type JobTableViewConfig = z.infer<typeof JobTableViewConfigSchema>

export const DEFAULT_JOB_TABLE_VIEW_NAME_KEY: TranslationKey =
  "common.jobTable.defaultViewName"

export const DEFAULT_JOB_TABLE_VIEW_CONFIG: JobTableViewConfig = {
  columns: [
    "jobNumber",
    "status",
    "customerName",
    "bikeBrand",
    "handInDate",
    "assignedMechanic",
    "labels",
    "totalOrderValue",
    "updatedAt",
  ],
  sorts: [{ column: "updatedAt", direction: "desc" }],
  filter: { conjunction: "and", children: [] },
  pageSize: 50,
}

export const JOB_TABLE_FILTERABLE_COLUMNS = [
  ...JOB_TABLE_TEXT_FILTER_COLUMNS,
  ...JOB_TABLE_NUMBER_FILTER_COLUMNS,
  ...JOB_TABLE_DATE_FILTER_COLUMNS,
  ...JOB_TABLE_OPTION_FILTER_COLUMNS,
  ...JOB_TABLE_BOOLEAN_FILTER_COLUMNS,
] as const

export type JobTableFilterableColumn =
  (typeof JOB_TABLE_FILTERABLE_COLUMNS)[number]

/** A new, not yet filled in, rule for a column (the backend ignores it). */
export function createJobTableFilterRule(
  id: string,
  column: JobTableFilterableColumn,
): JobTableFilterRule {
  if (isJobTableTextFilterColumn(column)) {
    return { id, kind: "text", column, operator: "contains", value: "" }
  }
  if (isJobTableNumberFilterColumn(column)) {
    return { id, kind: "number", column, operator: "eq", value: null }
  }
  if (isJobTableDateFilterColumn(column)) {
    return { id, kind: "date", column, operator: "is", value: null }
  }
  if (isJobTableOptionFilterColumn(column)) {
    return { id, kind: "options", column, operator: "isAnyOf", value: [] }
  }
  if (isJobTableBooleanFilterColumn(column)) {
    return { id, kind: "boolean", column, operator: "is", value: true }
  }
  throw new Error(`Column ${column} is not filterable`)
}

/** Counts the rules in a filter, including those inside nested groups. */
export function countJobTableFilterRules(filter: JobTableRootFilter): number {
  return filter.children.reduce(
    (count, child) => count + (child.kind === "group" ? child.rules.length : 1),
    0,
  )
}

// ── API contract ────────────────────────────────────────────────────────────

export const CreateJobTableViewSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common.validation.nameNotEmpty" satisfies TranslationKey)
    .max(100),
})

export const UpdateJobTableViewSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "common.validation.nameNotEmpty" satisfies TranslationKey)
    .max(100)
    .optional(),
  config: JobTableViewConfigSchema.optional(),
})

export const ReorderJobTableViewsSchema = z.object({
  viewIds: z.array(z.guid()).min(1),
})

export const FindJobTableRowsQuerySchema = z.object({
  page: z.coerce.number<number>().int().min(1).default(1),
  search: z.string().trim().max(200).optional(),
})

export type CreateJobTableView = z.infer<typeof CreateJobTableViewSchema>
export type UpdateJobTableView = z.infer<typeof UpdateJobTableViewSchema>
export type ReorderJobTableViews = z.infer<typeof ReorderJobTableViewsSchema>
export type FindJobTableRowsQuery = z.infer<typeof FindJobTableRowsQuerySchema>

export interface JobTableView {
  id: string
  name: string
  position: number
  config: JobTableViewConfig
}

export interface JobTableWorkItem {
  name: string
  isDone: boolean
}

export interface JobTableRow extends JobSummary {
  createdAt: Date
  customerEmail: string | null
  customerComment: string | null
  totalLaborCost: number | null
  totalProductCost: number | null
  /** Accepted work (packages, part actions, manual actions, washes). */
  work: JobTableWorkItem[]
  /** Names of the accepted or done packages, the ones the package filter matches. */
  packages: string[]
}

export interface JobTableRowsPage {
  rows: JobTableRow[]
  totalCount: number
  totalPages: number
  page: number
  pageSize: JobTablePageSize
}

export interface JobTableFilterOption {
  value: string
  label: string
  /** Hex colour for labels; null for everything else. */
  color: string | null
}

/** Selectable values for every option-filter column, built by the backend. */
export type JobTableFilterOptions = Record<
  JobTableOptionFilterColumn,
  JobTableFilterOption[]
>
